import { storeData, getStoredData } from "../utils/localStorage";

class LambdaService {
  constructor() {
    // Map of slugs to their endpoint configurations
    this.endpoints = {
      targetCustomerNames: {
        url: "https://pmq4kwc2b5mzxwliib5d673mlm0iarlk.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["input_text"],
      },
      targetCustomerDemographics: {
        url: "https://etva2f2dpubeng3lpc3jq6ycua0bggiu.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["target_customer"],
      },
      targetCustomerMarket: {
        url: "https://a7ld72gy4sqjkpiagpdoil7zuy0puyol.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["target_customer"],
      },
      targetCustomerOutreachAreas: {
        url: "https://vdimslsqlnsgetrzwvkvzlo47e0kyqsw.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["target_customer"],
      },
      opportunityMapping: {
        url: "https://56pk7bwa4s5lg4xlev2tp557xq0uwegw.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["target_customer", "action"],
      },
      contactList: {
        url: "https://sgwl723q6cd42bpfilupazvs240digac.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["platform", "target_url", "target_customer"],
      },
      transcriptAnalysis: {
        url: "https://bzbw53eoxeuxihizyrpbm3c66q0zlwbe.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["transcript", "researchPrompt"],
      },
      surveyLanguage: {
        url: "https://urddwppw3p6fu22a7nm7bciqzq0iefwb.lambda-url.us-east-2.on.aws",
        method: "GET",
        params: ["survey_language"],
      },
    };

    this.passcode = "bFn55ry7BU";
  }

  // Generic method to call any Lambda endpoint by slug
  async callBySlug(slug, parameters = {}, abortSignal = null) {
    const endpoint = this.endpoints[slug];

    if (!endpoint) {
      throw new Error(`Unknown endpoint slug: ${slug}`);
    }

    // Validate required parameters
    const missingParams = endpoint.params.filter(
      (param) => !(param in parameters)
    );
    if (missingParams.length > 0) {
      throw new Error(
        `Missing required parameters for ${slug}: ${missingParams.join(", ")}`
      );
    }

    // Build query parameters
    const queryParams = {};
    endpoint.params.forEach((param) => {
      queryParams[param] = parameters[param];
    });

    return this.callLambda(
      endpoint.url,
      endpoint.method,
      queryParams,
      null,
      {},
      abortSignal
    );
  }

  // Generic method to call Lambda with full URL
  async callLambda(
    url,
    method = "GET",
    queryParams = {},
    body = null,
    customHeaders = {},
    abortSignal = null
  ) {
    const maxRetries = 8; // Increased retries
    const baseDelay = 3000; // 3 seconds base delay

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const urlObj = new URL(url);

        // Add query parameters
        Object.keys(queryParams).forEach((key) => {
          urlObj.searchParams.append(key, queryParams[key]);
        });

        // Always try real calls - no mock fallback

        const headers = {
          "Content-Type": "application/json",
          passcode: this.passcode,
          ...customHeaders,
        };

        console.log(
          `Making real Lambda call to: ${urlObj.toString()} (attempt ${attempt}/${maxRetries})`
        );
        console.log("Request headers:", headers);
        console.log("Query parameters:", queryParams);

        const response = await fetch(urlObj.toString(), {
          method,
          headers,
          body: body ? JSON.stringify(body) : null,
          // Use provided abortSignal or fallback to timeout
          signal: abortSignal || AbortSignal.timeout(120000), // 120 second timeout
        });

        console.log("Lambda response status:", response.status);
        console.log(
          "Lambda response headers:",
          Object.fromEntries(response.headers.entries())
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Lambda response error:", errorText);
          throw new Error(
            `Lambda call failed: ${response.status} ${response.statusText} - ${errorText}`
          );
        }

        // Try to parse JSON response
        let data;
        let responseText;
        try {
          responseText = await response.text();
          console.log("Raw response text:", responseText);

          // Check if response is complete JSON
          if (
            !responseText.trim().endsWith("}") &&
            !responseText.trim().endsWith("]")
          ) {
            throw new Error("Incomplete JSON response - likely timeout");
          }

          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error("JSON parsing error:", parseError);
          console.error(
            "Raw response:",
            responseText || "Could not read response"
          );

          if (attempt < maxRetries) {
            console.log(
              `Retrying in ${baseDelay * attempt}ms... (attempt ${
                attempt + 1
              }/${maxRetries})`
            );
            await new Promise((resolve) =>
              setTimeout(resolve, baseDelay * attempt)
            );
            continue;
          } else {
            throw new Error(
              `Failed to parse JSON response after ${maxRetries} attempts: ${parseError.message}`
            );
          }
        }

        console.log("Real Lambda response data:", data);
        console.log("Response data type:", typeof data);
        console.log(
          "Response data length:",
          data ? Object.keys(data).length : "null/undefined"
        );

        // Check if response is empty or null
        if (
          !data ||
          data === null ||
          (typeof data === "object" && Object.keys(data).length === 0)
        ) {
          console.warn("⚠️ Lambda returned empty/null response");
          console.warn("Response:", data);
        }

        // Store response in localStorage for later use
        this.storeLambdaResponse(url, data);

        return data;
      } catch (error) {
        console.error(
          `Lambda service error (attempt ${attempt}/${maxRetries}):`,
          error
        );

        // Show clear error message for CORS issues
        if (
          error.message.includes("CORS") ||
          error.message.includes("Failed to fetch")
        ) {
          console.error(
            "❌ CORS ERROR: The AWS Lambda function needs CORS headers configured."
          );
          console.error("Add these headers to your Lambda function response:");
          console.error(`
Access-Control-Allow-Origin: *
Access-Control-Allow-Methods: GET, POST, OPTIONS
Access-Control-Allow-Headers: Content-Type, passcode
          `);
          throw new Error(
            "CORS Error: Lambda function needs CORS headers configured. Check console for details."
          );
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error;
        }

        // Exponential backoff with jitter
        const delay = Math.min(
          baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000,
          30000
        );
        console.log(
          `Retrying in ${Math.round(delay)}ms... (attempt ${
            attempt + 1
          }/${maxRetries})`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // Mock response for development/testing
  getMockResponse(url, queryParams) {
    const mockResponses = {
      targetCustomerNames: {
        data: [
          "Women aged 25-45 who need hair styling services",
          "Professional women seeking convenient beauty services",
          "Brides and bridal parties needing wedding hair styling",
          "Women with busy schedules who value time-saving beauty services",
        ],
        message: "Mock response for targetCustomerNames",
      },
      targetCustomerDemographics: {
        data: {
          ageRange: "25-45",
          gender: "Female",
          income: "Middle to upper-middle class",
          lifestyle: "Busy professionals and special occasion seekers",
        },
        message: "Mock response for targetCustomerDemographics",
      },
      targetCustomerMarket: {
        data: {
          marketSize: "Large and growing",
          competition: "Moderate",
          trends: "Increasing demand for convenient beauty services",
        },
        message: "Mock response for targetCustomerMarket",
      },
    };

    // Determine which mock response to use based on the URL
    let mockKey = "targetCustomerNames"; // default
    if (url.includes("etva2f2dpubeng3lpc3jq6ycua0bggiu"))
      mockKey = "targetCustomerDemographics";
    if (url.includes("a7ld72gy4sqjkpiagpdoil7zuy0puyol"))
      mockKey = "targetCustomerMarket";
    if (url.includes("vdimslsqlnsgetrzwvkvzlo47e0kyqsw"))
      mockKey = "targetCustomerOutreachAreas";
    if (url.includes("56pk7bwa4s5lg4xlev2tp557xq0uwegw"))
      mockKey = "opportunityMapping";
    if (url.includes("sgwl723q6cd42bpfilupazvs240digac"))
      mockKey = "contactList";
    if (url.includes("bzbw53eoxeuxihizyrpbm3c66q0zlwbe"))
      mockKey = "transcriptAnalysis";
    if (url.includes("urddwppw3p6fu22a7nm7bciqzq0iefwb"))
      mockKey = "surveyLanguage";

    const mockResponse = mockResponses[mockKey] || {
      data: `Mock response for ${queryParams.input_text || "Lambda call"}`,
      message: "Mock response for development",
    };

    // Store mock response
    this.storeLambdaResponse(url, mockResponse);

    return mockResponse;
  }

  // Store Lambda responses in localStorage
  storeLambdaResponse(url, data) {
    const existingData = getStoredData() || {};
    const lambdaResponses = existingData.lambdaResponses || {};

    // Use URL as key for storage
    lambdaResponses[url] = {
      data,
      timestamp: new Date().toISOString(),
    };

    storeData("lambdaResponses", lambdaResponses);
  }

  // Get stored Lambda response by URL
  getStoredLambdaResponse(url) {
    const data = getStoredData();
    return data?.lambdaResponses?.[url];
  }

  // Get stored response by slug
  getStoredResponseBySlug(slug) {
    const endpoint = this.endpoints[slug];
    if (!endpoint) return null;
    return this.getStoredLambdaResponse(endpoint.url);
  }

  // Get all stored Lambda responses
  getAllStoredResponses() {
    const data = getStoredData();
    return data?.lambdaResponses || {};
  }

  // Clear all Lambda responses
  clearLambdaResponses() {
    const data = getStoredData() || {};
    delete data.lambdaResponses;
    storeData("lambdaResponses", data);
  }

  // Method to prepare data for final GQL endpoint
  prepareDataForGQL() {
    const responses = this.getAllStoredResponses();
    return {
      lambdaResponses: responses,
      timestamp: new Date().toISOString(),
    };
  }

  // Convenience methods for each specific endpoint
  async getTargetCustomerNames(inputText) {
    return this.callBySlug("targetCustomerNames", { input_text: inputText });
  }

  async getTargetCustomerDemographics(targetCustomer) {
    return this.callBySlug("targetCustomerDemographics", {
      target_customer: targetCustomer,
    });
  }

  async getTargetCustomerMarket(targetCustomer) {
    return this.callBySlug("targetCustomerMarket", {
      target_customer: targetCustomer,
    });
  }

  async getTargetCustomerOutreachAreas(targetCustomer) {
    return this.callBySlug("targetCustomerOutreachAreas", {
      target_customer: targetCustomer,
    });
  }

  async getOpportunityMapping(targetCustomer, action) {
    return this.callBySlug("opportunityMapping", {
      target_customer: targetCustomer,
      action: action,
    });
  }

  async getContactList(
    platform,
    targetUrl,
    targetCustomer,
    abortSignal = null
  ) {
    // Create a custom timeout for contactList calls (10 minutes)
    const contactListTimeout = abortSignal || AbortSignal.timeout(600000); // 5 minutes

    return this.callBySlug(
      "contactList",
      {
        platform: platform,
        target_url: targetUrl,
        target_customer: targetCustomer,
      },
      contactListTimeout
    );
  }

  async getTranscriptAnalysis(transcript, researchPrompt) {
    return this.callBySlug("transcriptAnalysis", {
      transcript: transcript,
      researchPrompt: researchPrompt,
    });
  }

  async getSurveyLanguage(surveyLanguage) {
    return this.callBySlug("surveyLanguage", {
      survey_language: surveyLanguage,
    });
  }
}

export default new LambdaService();
